import { useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
// `@hookform/resolvers` v5 consolidated to a single `./zod` path that
// auto-detects whether the schema is Zod 3 or Zod 4 — no subpath needed.
// (v4 of the resolvers package only spoke Zod 3, which is why it broke
// Zod 4 schemas with Unhandled Rejections under the previous pin.)
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, Loader2 } from 'lucide-react';

import { useAuth } from '@/lib/auth/auth-provider';
import type { MfaChallengeError } from '@/lib/auth/errors';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { MfaChallengeStep } from './MfaChallengeStep';

const SignInSchema = z.object({
  // Zod 4: format builders moved to the top level. z.email() replaces the
  // deprecated z.string().email(). Use .pipe() so the "required" error
  // surfaces before the "valid email" error when the field is blank.
  email: z.string().min(1, 'Email is required').pipe(z.email('Enter a valid email')),
  password: z.string().min(1, 'Password is required'),
});

type SignInInput = z.infer<typeof SignInSchema>;

interface MfaChallenge {
  readonly session: string;
  readonly username: string;
}

/**
 * Where to land the user after a successful signin. Honours, in
 * priority order:
 *   1. `?bc_claim=<id>` → `/app/bc-claim?id=…` (BC-initiated install
 *      flow, ADR 0011).
 *   2. `?next=<encoded-path>` → that path (set by `requireAuth` when
 *      it bounces signed-out users back here).
 *   3. Default `/app`.
 *
 * `next` is constrained to same-origin paths starting with `/app/`
 * or `/auth/` to prevent open-redirect attacks; anything else falls
 * back to `/app`.
 */
function postSignInTarget(params: URLSearchParams): string {
  const bcClaim = params.get('bc_claim');
  if (bcClaim) {
    return `/app/bc-claim?id=${encodeURIComponent(bcClaim)}`;
  }
  const next = params.get('next');
  if (next && (next.startsWith('/app/') || next.startsWith('/auth/') || next === '/app')) {
    return next;
  }
  return '/app';
}

export function SignInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signIn, respondToMfaChallenge } = useAuth();
  const [topError, setTopError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  // Local `submitting` state instead of `form.formState.isSubmitting`
  // so we can keep the button disabled past the await — through the
  // `navigate()` call and until the route actually unmounts the page —
  // per [ADR 0021](../../../../docs/adr/0021-frontend-feedback-loop-polish.md).
  // Otherwise the button flashes enabled between the resolved promise
  // and the route push, inviting a double-submit.
  const [submitting, setSubmitting] = useState(false);
  const postSignIn = postSignInTarget(params);

  const form = useForm<SignInInput>({
    resolver: zodResolver(SignInSchema),
    // Email may arrive prefilled from /forgot after a successful reset, or
    // from /signin?email=... links elsewhere. Falls back to empty.
    defaultValues: { email: params.get('email') ?? '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setTopError(null);
    setSubmitting(true);
    const result = await signIn(values.email, values.password);

    if (result.isOk()) {
      const outcome = result.value;
      if (outcome.kind === 'authenticated') {
        // Navigating away — keep `submitting` true so the button stays
        // disabled until unmount. Don't reset.
        void navigate(postSignIn);
        return;
      }
      // mfa-required: pivot to the MFA code step. The sign-in button
      // unmounts (we render `<MfaChallengeStep>` instead), so resetting
      // submitting is mostly defensive — but explicit is clearer.
      setSubmitting(false);
      setMfaChallenge({ session: outcome.session, username: outcome.username });
      return;
    }

    const error = result.error;
    switch (error.kind) {
      case 'user-not-confirmed':
        void navigate(`/confirm?email=${encodeURIComponent(error.email)}`);
        return;
      case 'password-reset-required':
        void navigate(`/forgot?email=${encodeURIComponent(error.email)}`);
        return;
      case 'invalid-credentials':
        setSubmitting(false);
        setTopError('Incorrect email or password.');
        return;
      case 'too-many-requests':
        setSubmitting(false);
        setTopError('Too many attempts. Please try again in a few minutes.');
        return;
      case 'unknown':
        setSubmitting(false);
        setTopError(error.message);
        return;
    }
  });

  // No <form> elements per apps/web/CLAUDE.md §6 — wire Enter manually on
  // the last input so keyboard submit still works.
  const submitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      void onSubmit();
    }
  };

  const emailError = form.formState.errors.email?.message;
  const passwordError = form.formState.errors.password?.message;

  const handleMfaSubmit = async (code: string): Promise<void> => {
    if (!mfaChallenge) return;
    setTopError(null);
    const result = await respondToMfaChallenge(mfaChallenge.username, mfaChallenge.session, code);
    if (result.isOk()) {
      void navigate(postSignIn);
      return;
    }
    setTopError(mfaChallengeErrorMessage(result.error));
  };

  const handleMfaBack = () => {
    setMfaChallenge(null);
    setTopError(null);
  };

  return (
    <main className="bg-muted/30 flex min-h-screen items-center justify-center px-4 py-12">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2">
          <CardTitle className="text-xl">
            {mfaChallenge ? 'Two-factor authentication' : 'Sign in to Mailchute.ai'}
          </CardTitle>
          <CardDescription>
            {mfaChallenge ? (
              <>
                Wrong account?{' '}
                <button
                  type="button"
                  onClick={handleMfaBack}
                  className="text-primary hover:underline"
                >
                  Sign in as someone else
                </button>
              </>
            ) : (
              <>
                New here?{' '}
                <Link to="/signup" className="text-primary hover:underline">
                  Create an account
                </Link>
              </>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {mfaChallenge ? (
            <MfaChallengeStep onSubmit={handleMfaSubmit} topError={topError} />
          ) : (
            <>
              {topError ? (
                <Alert variant="destructive">
                  <AlertDescription>{topError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="signin-email">Email</Label>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? 'signin-email-error' : undefined}
                  {...form.register('email')}
                />
                {emailError ? (
                  <p id="signin-email-error" className="text-destructive text-xs">
                    {emailError}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="signin-password">Password</Label>
                  <Link
                    to="/forgot"
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="signin-password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={Boolean(passwordError)}
                  aria-describedby={passwordError ? 'signin-password-error' : undefined}
                  onKeyDown={submitOnEnter}
                  {...form.register('password')}
                />
                {passwordError ? (
                  <p id="signin-password-error" className="text-destructive text-xs">
                    {passwordError}
                  </p>
                ) : null}
              </div>

              <Button
                type="button"
                onClick={() => void onSubmit()}
                disabled={submitting}
                className="w-full"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign in <ArrowRight className="size-4" aria-hidden />
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function mfaChallengeErrorMessage(error: MfaChallengeError): string {
  switch (error.kind) {
    case 'code-mismatch':
      return 'That code is incorrect. Check your authenticator app and try again.';
    case 'code-expired':
      return 'That code has expired. Authenticator codes only last 30 seconds.';
    case 'invalid-code-format':
      return 'Two-factor codes are six digits.';
    case 'too-many-requests':
      return 'Too many attempts. Please wait a minute before trying again.';
    case 'session-expired':
      return 'Sign-in session expired. Please sign in again.';
    case 'unknown':
      return error.message;
  }
}
